import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ProgressRef, ProgressService } from 'app/shared';
import { finalize } from 'rxjs/operators';
import { NotificationEventV2, NotificationTypes } from '../notification.model';
import { NotificationService } from '../notification.service';
import { UIService } from 'app/shared/ui.service';
import { SvgMap } from 'app/shared/components/svg/uds-svg-map';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'dp-event-tab-v2',
  templateUrl: './event-tab-v2.component.html',
  styleUrls: ['./event-tab.component.scss'],
})
export class EventTabV2Component implements OnInit {
  SvgMap = SvgMap;
  throttle = 0;
  limit = 50;
  distance = 2;
  scrollThrottle = 50;
  loading = true;
  page: number;
  progress: ProgressRef;
  notificationEvents: NotificationEventV2[] = [];

  @ViewChild('contentZone') contentZone: ElementRef;

  @Input() set opened(open: boolean) {
    if (open) {
      // defer so that contentZone is available after matTabContent is instantiated
      setTimeout(() => {
        this.init();
      });
    }
  }

  @Input() hasUnread: boolean;
  @Input() type: NotificationTypes;

  @Output() allRead = new EventEmitter<void>();
  @Output() partialRead = new EventEmitter<void>();

  constructor(
    private service: NotificationService,
    private progressService: ProgressService,
    private uiService: UIService,
    private changeDetector: ChangeDetectorRef,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    // nothing special on init – everything is driven by [opened]
  }

  init(): void {
    if (!this.contentZone) {
      // defensive: if contentZone isn't ready yet, just bail – opened() will run again
      return;
    }

    const progress = this.progressService.showProgress(this.contentZone);
    this.loading = true;
    this.contentZone.nativeElement.scrollTop = 0;
    this.page = 1;

    // IMPORTANT: this uses the V2 endpoint & type
    this.service
      .getEventsV2(this.page, this.limit, this.type)
      .pipe(
        finalize(() => {
          this.progressService.detach(progress);
          this.loading = false;
        })
      )
      .subscribe(
        (events) => {
          this.notificationEvents = events.map((event) => ({
            ...event,
            notificationIds: [Number(event.id)],
          }));
          this.refresh();
        },
        (error) => {
          this.uiService.showSnackbar(error.message, null, {
            duration: 3000,
            panelClass: 'warn',
          });
        }
      );
  }

  onScroll(): void {
    this.loading = true;

    this.service
      .getEventsV2(++this.page, this.limit, this.type)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(
        (items) => {
          const mappedItems = items.map((event) => ({
            ...event,
            notificationIds: [Number(event.id)],
          }));
          this.notificationEvents.push(...mappedItems);
          this.refresh();
        },
        (error) => {
          this.uiService.showSnackbar(error.message, null, {
            duration: 3000,
            panelClass: 'warn',
          });
        }
      );
  }

  // called when a single notification is marked as read in the detail component
  isRead(event: NotificationEventV2) {
    event.isRead = true;
    this.partialRead.emit();
    this.refresh();
  }

  markAllRead() {
    const progress = this.progressService.showProgress(this.contentZone);

    this.service
      .markAllRead(this.type)
      .pipe(finalize(() => this.progressService.detach(progress)))
      .subscribe(
        () => {
          this.allRead.emit();
          this.notificationEvents.forEach((event) => (event.isRead = true));
          this.refresh();
        },
        (error) => {
          this.uiService.showSnackbar(error.message, null, {
            duration: 3000,
            panelClass: 'warn',
          });
        }
      );
  }

  // propagate dialog close via NotificationService if needed
  dismissIt() {
    this.service.closeNotificationDlg.next(true);
  }

  private refresh() {
    setTimeout(() => {
      this.changeDetector.detectChanges();
    });
  }
}
